'use client';

import { isBefore } from 'date-fns';
import { useState } from 'react';
import { useTradeContext } from '../../../../components/ui/TradePageClient/TradePageClient';
import WChanceStaging from '../../../../components/ui/WChanceStaging/WChanceStaging';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import { newDate } from '../../../../utils/date';
import type { LotteryOptionGoodsForm } from '../../types';
import { createStagingInfo } from '../../utils';
import { isValidOptionNameValue } from '../../utils/isValidOptionNameValue';
import OptionNamedCollectionList from '../OptionNamedCollectionList/OptionNamedCollectionList';
import OptionNamedSelectableStepNamedCollectionList from '../OptionNamedSelectableStepNamedCollectionList/OptionNamedSelectableStepNamedCollectionList';
import OptionNamedSelectableStepQuantityCollectionList from '../OptionNamedSelectableStepQuantityCollectionList/OptionNamedSelectableStepQuantityCollectionList';
import OptionSelectableCollectionList from '../OptionSelectableCollectionList/OptionSelectableCollectionList';
import styles from './OptionCollectionList.module.scss';

// エラーメッセージ定数
const INVALID_CHARACTER_ERROR_MESSAGE =
  '利用できない文字（記号など）が含まれています。ひらがな、カタカナ、漢字、英数字（半角・全角）のみが利用可能です。一部の旧字体などは利用できません。';

export type OptionCollectionListProps = {
  optionFormData: LotteryOptionGoodsForm[];
  isEditMode?: boolean;
  step?: 'quantity' | 'name';
  onStepChange?: (step: 'quantity' | 'name') => void;
  optionDefaultSetType: LotteryOptionGoodsForm['optionDefaultSetType'];
};

function OptionCollectionList({
  optionFormData,
  isEditMode = false,
  step,
  onStepChange: _onStepChange,
  optionDefaultSetType,
}: OptionCollectionListProps) {
  const { selectedItems } = useTradeContext();
  const [inputErrors, setInputErrors] = useState<Record<string, boolean>>(() =>
    createInitialInputErrors(optionFormData),
  );
  const [prevStep, setPrevStep] = useState(step);

  // 宛名＆選べるの数量の画面（締め切り前）には宛名の入力欄が無いため、宛名の画面に戻るたびに入力欄が作り直され、中身は保存済みの値に戻る。
  // エラーだけが前の入力のまま残ると、赤い文字とボタンの状態が食い違うため、ステップが変わったら保存済みの値から作り直す。
  if (step !== prevStep) {
    setPrevStep(step);
    setInputErrors(createInitialInputErrors(optionFormData));
  }

  // 入力値変更時のハンドラー
  const handleInputChange = (inputKey: string, value: string) => {
    const isValid = isValidOptionNameValue(value);
    setInputErrors((prev) => ({
      ...prev,
      [inputKey]: !isValid,
    }));
  };
  const formattedItems = (items: Array<LotteryOptionGoodsForm>) => {
    const result = items.map((item) => {
      const stagingKey = item.label.stagingKey || '';
      const stagingInfo = createStagingInfo(stagingKey);

      const formattedItem = {
        ...item,
        ...stagingInfo,
      };
      return formattedItem;
    });

    return result;
  };

  // 宛名の入力欄のプレースホルダーを取得する関数
  const getNamedInputPlaceholder = () => {
    if (optionDefaultSetType === 'name') return 'ご希望の宛名を入力してください';
    return 'ご希望の内容を入力してください';
  };

  // 「宛名選択式オプション」の数量入力UIを表示すべきかを判定する。
  // - オプション種別が `named_selectable` であること
  // - 画面ステップが `quantity` であること
  // - 申込期限前であること（期限後は数量編集させない）
  const isNamedSelectableQuantityStep = (formItem: LotteryOptionGoodsForm) => {
    if (formItem.optionInfo.type !== 'named_selectable') return false;
    if (step !== 'quantity') return false;
    return !isPastDeadline(formItem);
  };

  // optionInfo配下の配列をレンダリングする関数
  const renderOptionItems = (formItem: LotteryOptionGoodsForm, editMode: boolean) => {
    const { optionInfo } = formItem;
    const isDeadlinePassed = isPastDeadline(formItem);
    const currentStep = formItem.optionInfo.type === 'named_selectable' && isDeadlinePassed ? 'name' : step;

    // 自動入力数を表示するかどうかを判定する関数
    const shouldShowAutoInputCount = () => {
      if (!isDeadlinePassed) return false;
      if (!formItem.optionInfo.named) return false;
      return formItem.optionInfo.named.some((item) => item.isSystemInput === true);
    };

    switch (optionInfo.type) {
      case 'named':
        return (
          <OptionNamedCollectionList
            formItem={formItem}
            optionInfo={optionInfo}
            editMode={editMode}
            isDeadlinePassed={isDeadlinePassed}
            inputErrors={inputErrors}
            onInputChange={handleInputChange}
            getNamedInputPlaceholder={getNamedInputPlaceholder}
            shouldShowAutoInputCount={shouldShowAutoInputCount}
            invalidCharacterErrorMessage={INVALID_CHARACTER_ERROR_MESSAGE}
            staging={staging}
          />
        );

      case 'selectable':
        if (optionInfo.selectable && optionInfo.selectable.length > 0) {
          return (
            <OptionSelectableCollectionList
              formItem={formItem}
              selectableItems={optionInfo.selectable}
              isDeadlinePassed={isDeadlinePassed}
              selectedItems={selectedItems}
              stagingHeader={stagingHeader}
            />
          );
        }
        break;

      case 'named_selectable':
        if (!optionInfo.namedSelectable) return <></>;
        // ステップが'quantity'の場合はselectableと同じ表示
        if (currentStep === 'quantity') {
          return (
            <OptionNamedSelectableStepQuantityCollectionList
              formItem={formItem}
              namedSelectableItems={optionInfo.namedSelectable}
              isDeadlinePassed={isDeadlinePassed}
              selectedItems={selectedItems}
              inputErrors={inputErrors}
              onInputChange={handleInputChange}
              getNamedInputPlaceholder={getNamedInputPlaceholder}
              invalidCharacterErrorMessage={INVALID_CHARACTER_ERROR_MESSAGE}
              stagingHeader={stagingHeader}
            />
          );
        }

        // ステップが'name'の場合は選択した数量分の入力欄を表示
        if (currentStep === 'name') {
          return (
            <OptionNamedSelectableStepNamedCollectionList
              formItem={formItem}
              namedSelectableItems={optionInfo.namedSelectable}
              isDeadlinePassed={isDeadlinePassed}
              editMode={editMode}
              selectedItems={selectedItems}
              inputErrors={inputErrors}
              onInputChange={handleInputChange}
              getNamedInputPlaceholder={getNamedInputPlaceholder}
              invalidCharacterErrorMessage={INVALID_CHARACTER_ERROR_MESSAGE}
              staging={staging}
            />
          );
        }

        break;

      default:
        return null;
    }

    return null;
  };

  return (
    <div className={styles.optionCollectionList}>
      {formattedItems(optionFormData).map((formItem) => (
        <div
          key={formItem.id}
          className={
            formItem.optionInfo.type === 'selectable' || isNamedSelectableQuantityStep(formItem)
              ? styles.optionItemGroupQuantity
              : undefined
          }
        >
          {renderOptionItems(formItem, isEditMode)}
        </div>
      ))}
    </div>
  );
}

// 期日チェック
function isPastDeadline(formItem: LotteryOptionGoodsForm) {
  const now = newDate();
  const deadline = newDate(formItem.optionCutoffAt);
  return isBefore(deadline, now);
}

// 修正前の判定で保存された宛名には、今は使えない字（鷗 など）が入っている場合がある。
// 入力を変えたときだけ判定すると、編集画面を開いた時点では気づけないため、保存済みの宛名も最初に判定する。
// 締め切り後は入力欄が disabled で直せず、自動入力の値には記号が入る場合もあるため判定しない。
// キーは各入力欄の部品（OptionNamedCollectionList など）の inputKey と揃える。
function createInitialInputErrors(optionFormData: LotteryOptionGoodsForm[]) {
  const errors: Record<string, boolean> = {};
  optionFormData.forEach((formItem) => {
    if (isPastDeadline(formItem)) return;

    formItem.optionInfo.named?.forEach((namedItem, inputIndex) => {
      errors[`named-${formItem.id}-${inputIndex}`] = !isValidOptionNameValue(namedItem.value || '');
    });

    formItem.optionInfo.namedSelectable?.forEach((namedSelectableItem, index) => {
      namedSelectableItem.notes?.forEach((noteItem, inputIndex) => {
        errors[`named_selectable-${formItem.id}-${index}-${inputIndex}`] = !isValidOptionNameValue(
          noteItem.value || '',
        );
      });
    });
  });
  return errors;
}

function getRankStyle(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1;
    case 2:
      return styles.rank2;
    case 3:
      return styles.rank3;
    default:
      return styles.defaultRankImage;
  }
}

function getRankStyleHeader(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1HeaderQuantity;
    case 2:
      return styles.rank2HeaderQuantity;
    case 3:
      return styles.rank3HeaderQuantity;
    default:
      return styles.defaultRankImageHeaderQuantity;
  }
}

function staging(formItem: LotteryOptionGoodsForm) {
  const stagingKey = formItem.label.stagingKey;

  // X連特典
  if (formItem.label.benefitMultiDrawCount && formItem.label.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitWrapper}>
        <span className={fontsStyles.montserrat}>{formItem.label.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }

  if (formItem.label.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'default'} />;
  }
  if (stagingKey) {
    const stagingInfo = createStagingInfo(stagingKey);
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

function stagingHeader(formItem: LotteryOptionGoodsForm) {
  const stagingKey = formItem.label.stagingKey;

  // X連特典
  if (formItem.label.benefitMultiDrawCount && formItem.label.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitHeaderQuantityWrapper}>
        <span className={fontsStyles.montserrat}>{formItem.label.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }

  if (formItem.label.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'large'} />;
  }
  if (stagingKey) {
    const stagingInfo = createStagingInfo(stagingKey);
    return (
      <div className={`${styles.stagingRankHeaderQuantity} ${getRankStyleHeader(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingTextHeaderQuantity} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

export default OptionCollectionList;
